import { Button } from "@/components/ui/Button";
import CustomSelect from "@/components/ui/CustomSelect";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/Drawer";
import { Label } from "@/components/ui/Label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { Separator } from "@/components/ui/Separator";
import { AREA } from "@/constants/ListingPropertyAreas";
import { useSearch } from "@/contexts/SearchContext";
import { formatNumber } from "@/lib/utils";
import clsx from "clsx";
import { ChevronDown } from "lucide-react";
import { useTranslations } from "next-intl";
import { RefObject, useMemo, useRef } from "react";
import { useOnClickOutside, useToggle } from "usehooks-ts";
import { PropertyFilterProps } from "./type";

export const PropertyAreaFilter = ({ variant, title }: PropertyFilterProps) => {
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const drawerContentRef = useRef<HTMLDivElement>(null);

  const [open, toggleOpen, setOpen] = useToggle();
  const t = useTranslations("shared");

  useOnClickOutside(popoverContentRef as RefObject<HTMLElement>, (event) => {
    const target = event.target as HTMLElement;
    const isSelectContent = target.closest('[role="listbox"]');

    if (!isSelectContent) {
      setOpen(false);
    }
  });

  useOnClickOutside(drawerContentRef as RefObject<HTMLElement>, (event) => {
    const target = event.target as HTMLElement;
    const isSelectContent = target.closest('[role="listbox"]');

    if (!isSelectContent) {
      setOpen(false);
    }
  });
  const { area, updateArea } = useSearch();

  const handleClear = () => {
    updateArea({ from: null, to: null });
  };

  const areaTitle = useMemo(() => {
    let title = "";

    if (!area.from && !area.to) {
      return t("words.area");
    }

    if (area.from) {
      title += `${t("words.sqft", { value: area.from })} - `;
    } else {
      title += `${t("words.any")} - `;
    }

    if (area.to) {
      title += `${t("words.sqft", { value: area.to })}`;
    } else {
      title += t("words.any");
    }

    return title;
  }, [area.from, area.to]);

  const Content = () => {
    return (
      <div
        className={clsx("py-1 sm:py-2 space-y-2 w-full", {
          "sm:!min-w-96": !title,
        })}
      >
        {title && <Label>{t("words.area")}</Label>}
        <div className="flex flex-col sm:flex-row  w-full items-center gap-2">
          <div className="space-y-2 w-full">
            {!title && <Label>{t("filters.area.min-area.label")}</Label>}
            <CustomSelect
              options={AREA.map((area) => ({
                label: t("words.sqft", { value: formatNumber(area) }),
                value: area.toString(),
              }))}
              className="w-full"
              value={
                area.from
                  ? {
                      label: t("words.sqft", {
                        value: formatNumber(area.from!),
                      }),
                      value: area.from.toString(),
                    }
                  : undefined
              }
              placeholder={t("filters.area.min-area.placeholder")}
              onChange={(option) =>
                option?.value && updateArea({ from: parseInt(option.value) })
              }
              size="sm"
            />
          </div>

          <div className="space-y-2 w-full">
            {!title && (
              <Label className="!mb-2">
                {t("filters.area.max-area.label")}
              </Label>
            )}
            <CustomSelect
              options={AREA.map((area) => ({
                label: t("words.sqft", { value: formatNumber(area) }),
                value: area.toString(),
              }))}
              className="w-full"
              value={
                area.to
                  ? {
                      label: t("words.sqft", {
                        value: formatNumber(area.to!),
                      }),
                      value: area.to.toString(),
                    }
                  : undefined
              }
              placeholder={t("filters.area.max-area.placeholder")}
              onChange={(option) =>
                option?.value && updateArea({ to: parseInt(option.value) })
              }
              size="sm"
            />
          </div>
        </div>

        {variant !== "plain" && (
          <>
            <Separator />
            <div className="space-x-2 flex items-end">
              <Button
                variant={"tertiary"}
                onClick={handleClear}
                className="w-full"
              >
                {t("filters.cta.clear")}
              </Button>
              <Button
                variant={"secondary"}
                onClick={() => setOpen(false)}
                className="w-full"
              >
                {t("filters.cta.done")}
              </Button>
            </div>
          </>
        )}
      </div>
    );
  };

  switch (variant) {
    case "popover":
      return (
        <Popover open={open}>
          <PopoverTrigger asChild className="h-full text-base">
            <Button
              variant={area.to || area.from ? "active" : "outline"}
              onClick={toggleOpen}
              className="min-w-fit"
            >
              {areaTitle}
              <ChevronDown className="size-4.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-full flex flex-col gap-4"
            ref={popoverContentRef}
          >
            <Content />
          </PopoverContent>
        </Popover>
      );
    case "plain":
      return <Content />;
    case "drawer":
      return (
        <Drawer open={open} key={"area-drawer"}>
          <DrawerTrigger asChild className="h-full text-base">
            <Button
              variant={area.to || area.from ? "active" : "outline"}
              onClick={() => setOpen(true)}
            >
              {areaTitle}
              <ChevronDown className="size-4.5" />
            </Button>
          </DrawerTrigger>
          <DrawerContent
            className="w-full flex flex-col gap-4 p-2"
            ref={drawerContentRef}
          >
            <DrawerTitle>{t("filters.area.title")}</DrawerTitle>
            <Content />
          </DrawerContent>
        </Drawer>
      );
  }
};
